import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@shared/api/client';

import type { GatewayActivationResult, GatewayTestResult, PaymentGateway } from '../_shared/types';
import { paymentGatewaysKey } from './queries';

/**
 * The five writes this surface owns.
 *
 * **No mutation auto-retries.** Four of these five re-point or remove the route
 * every payment in the product travels, and the fifth replaces a live
 * credential; a client that retried one of them on a timeout would be guessing
 * at an outcome the operator has to read.
 *
 * **Rotation writes a configuration row, not a gateway column.** The registry
 * carries the *name* of the key that holds the provider's secret and no column
 * for the secret itself, so the credential is replaced through the same
 * masked-and-rotated path every secret-classed setting takes. The response
 * carries no value, and nothing here stores one.
 *
 * **Deleting a gateway carries no `confirm=true`.** That flag is the wire half
 * of a *hard* removal and the contract declares it on the removals that destroy
 * stored bytes; a registry row is retired by a soft delete. Sending the flag
 * where the contract does not declare it would document a guard that does not
 * exist. The dialog is still required — it is the operator's guard.
 */

export interface GatewayWriteBody {
  name: string;
  base_url: string;
  callback_path: string;
  fee_bps: number;
}

function useGatewayInvalidation() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: paymentGatewaysKey() });
}

export function useRegisterGateway() {
  const invalidate = useGatewayInvalidation();

  return useMutation({
    mutationFn: (body: GatewayWriteBody) =>
      api.post<PaymentGateway>('finance', '/admin/paymentGateways', body),
    onSuccess: invalidate,
    retry: false,
  });
}

export function useUpdateGateway() {
  const invalidate = useGatewayInvalidation();

  return useMutation({
    mutationFn: ({ gatewayId, body }: { gatewayId: string; body: GatewayWriteBody }) =>
      api.put<PaymentGateway>('finance', `/admin/paymentGateways/${gatewayId}`, body),
    onSuccess: invalidate,
    retry: false,
  });
}

export function useDeleteGateway() {
  const invalidate = useGatewayInvalidation();

  return useMutation({
    mutationFn: (gatewayId: string) => api.delete<null>('finance', `/admin/paymentGateways/${gatewayId}`),
    onSuccess: invalidate,
    retry: false,
  });
}

export function useActivateGateway() {
  const invalidate = useGatewayInvalidation();

  return useMutation({
    mutationFn: (gatewayId: string) =>
      api.post<GatewayActivationResult>('finance', `/admin/paymentGateways/${gatewayId}:activate`),
    onSuccess: invalidate,
    retry: false,
  });
}

/**
 * The probe. **A failing test is a `200` carrying `ok: false`, not an error
 * status** — a failing test is the answer the operator asked for, and rendering
 * it as a request failure would say the question never reached the provider.
 *
 * It writes no payment, grants no entitlement, and appears in no report; the
 * dialog's own copy says so and this contract holds it to it. The registry is
 * still invalidated, because the probe stamps the row's last-tested outcome.
 */
export function useTestGateway() {
  const invalidate = useGatewayInvalidation();

  return useMutation({
    mutationFn: (gatewayId: string) =>
      api.post<GatewayTestResult>('finance', `/admin/paymentGateways/${gatewayId}:testTransaction`),
    onSuccess: invalidate,
    retry: false,
  });
}

/**
 * Credential rotation.
 *
 * The key is read off the row — the registry names it — and the value is read
 * from an uncontrolled field at the moment of the call. Neither the old value
 * nor the new one is held in component state, written to a cell, or put on an
 * attribute at any point in the flow.
 */
export function useRotateGatewayCredential() {
  const invalidate = useGatewayInvalidation();

  return useMutation({
    mutationFn: ({ configurationKey, value }: { configurationKey: string; value: string }) =>
      api.put<null>('finance', `/admin/configurations/${configurationKey}`, { value }),
    onSuccess: invalidate,
    retry: false,
  });
}
